import { useMemo, createElement } from "react";
import { firstLetterToUpperCase, getCexLabel } from "@cede/utils";
import { CAMPAIGN_STATUS, ParticipationFilter } from "@/components/features/campaigns/types";
import { ExchangeType, CampaignStatusType } from "@/components/features/campaigns/types";
import { ExchangeIcon } from "@/components/icons";
import { getSupportedHubExchangeIds, createRuleFilterOptions } from "@/utils/campaign";

interface UseCampaignsTableFiltersProps {
  selectedExchanges: ExchangeType[];
  selectedStatuses: CampaignStatusType[];
  selectedRules: string[];
  participationFilter: string | null;
}

export const useCampaignsTableFilters = ({
  selectedExchanges,
  selectedStatuses,
  selectedRules,
  participationFilter,
}: UseCampaignsTableFiltersProps) => {
  const exchangeFilterOptions = useMemo(() => {
    return getSupportedHubExchangeIds().map((exchangeId) => ({
      id: exchangeId,
      label: getCexLabel(exchangeId),
      checked: Array.isArray(selectedExchanges) ? selectedExchanges.includes(exchangeId) : false,
      icon: createElement(
        "div",
        { className: "w-4 h-4 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0" },
        createElement(ExchangeIcon, {
          size: 16,
          exchange: exchangeId,
        }),
      ),
    }));
  }, [selectedExchanges]);

  const statusFilterOptions = useMemo(() => {
    return Object.values(CAMPAIGN_STATUS).map((status) => ({
      id: status,
      label: firstLetterToUpperCase(status as string),
      checked: Array.isArray(selectedStatuses) ? selectedStatuses.includes(status) : false,
    }));
  }, [selectedStatuses]);

  const participationFilterOptions = useMemo(() => {
    return [
      {
        id: "all",
        label: "Show All",
        checked: participationFilter === null,
      },
      ...Object.entries(ParticipationFilter).map(([key, value]) => ({
        id: value,
        label: key === "PARTICIPATING" ? "Participating Only" : "Not Participating",
        checked: participationFilter === value,
      })),
    ];
  }, [participationFilter]);

  const ruleFilterOptions = useMemo(() => {
    return createRuleFilterOptions(selectedRules);
  }, [selectedRules]);

  return {
    exchangeFilterOptions,
    statusFilterOptions,
    participationFilterOptions,
    ruleFilterOptions,
  };
};
